import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.css';
import { Button, Form, Row, Col, Table, Pagination, Badge } from 'react-bootstrap';
import { 
  faSearch, faSave, faEye, faPaperPlane, faDownload, faFilter, faArrowLeft 
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import InputMask from 'react-input-mask';
import { format, parse } from 'date-fns';
import { useNavigate } from 'react-router-dom';

import Spinner from '../../../shared/spinner';
import CustomAlert from '../../../shared/customAlert';
import ScrollToTopButton from '../../../shared/scrollToTopButton';
import { ButtonAlterar } from '../../../shared/buttons';

// Services
import PromotoraService from '../../../services/promotoraService';
import OperacaoService from '../../../services/operacaoService';
import CessaoService from '../../../services/cessaoService';
import EmpregadorService from '../../../services/empregadorService';
import RemessaCessaoService from "../../../services/remessaCessaoService";

// DTOs / Models
import { Promotora } from '../../../shared/model/promotora';
import { Empregador } from '../../../shared/model/empregador';
import { OperacaoDTO } from '../../../shared/dto/operacaoDTO';
import { EmpregadorPromotoraCessaoDTO } from '../../../shared/dto/empregadorPromotoraCessaoDTO';
import { EmpregadorPromotoraDTO } from '../../../shared/dto/empregadorPromotoraDTO';
import { Cessao } from '../../../shared/model/cessao';

// =========================================================================
// COMPONENTE: Paginação Compacta
// =========================================================================
function PaginationCompacta({ page, totalPages, onPageChange }: { page: number; totalPages: number; onPageChange: (page: number) => void }) {
  const itens = [];
  const delta = 2;
  let start = Math.max(0, page - delta);
  let end = Math.min(totalPages - 1, page + delta);

  if (page - delta < 0) end = Math.min(totalPages - 1, end + (delta - page));
  if (page + delta > totalPages - 1) start = Math.max(0, start - (page + delta - (totalPages - 1)));

  itens.push(<Pagination.Item key={0} active={page === 0} onClick={() => onPageChange(0)}>1</Pagination.Item>);
  if (start > 1) itens.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
  for (let i = start; i <= end; i++) {
    if (i !== 0 && i !== totalPages - 1) {
      itens.push(<Pagination.Item key={i} active={i === page} onClick={() => onPageChange(i)}>{i + 1}</Pagination.Item>);
    }
  }
  if (end < totalPages - 2) itens.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
  if (totalPages > 1) {
    itens.push(<Pagination.Item key={totalPages - 1} active={page === totalPages - 1} onClick={() => onPageChange(totalPages - 1)}>{totalPages}</Pagination.Item>);
  }

  return (
    <Pagination>
      <Pagination.Prev onClick={() => onPageChange(Math.max(0, page - 1))} disabled={page === 0} />
      {itens}
      <Pagination.Next onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1} />
    </Pagination>
  );
}

// =========================================================================
// COMPONENTE PRINCIPAL: GerenciamentoCessoes
// =========================================================================
export default function GerenciamentoCessoes() {
  const navigate = useNavigate();
  
  // Controle de Telas Principais
  const [view, setView] = useState<'PROTOCOLOS' | 'LANCAMENTO'>('PROTOCOLOS');
  
  // Sub-estado de Navegação da Aba de Protocolos (1=Cessões, 2=Remessas, 3=Contratos JSON)
  const [protocoloStep, setProtocoloStep] = useState<1 | 2 | 3>(1);
  const [selectedCessaoRemessa, setSelectedCessaoRemessa] = useState<any>(null);

  // Sub-estado de Navegação da Aba de Lançamento (Tabela ou Configuração Manual)
  const [lancamentoSubView, setLancamentoSubView] = useState<'TABELA' | 'CONFIG'>('TABELA');
  
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{ message: string, type: 'success' | 'warning' | 'error' } | null>(null);

  const promotoraService = new PromotoraService();
  const operacaoService = new OperacaoService();
  const cessaoService = new CessaoService();
  const empregadorService = new EmpregadorService();
  const remessaCessaoService = new RemessaCessaoService();

  // --- ESTADOS: VISÃO 1 (PROTOCOLOS) ---
  const [cessoesAbaProtocolo, setCessoesAbaProtocolo] = useState<Cessao[]>([]);
  const [pageProtocolo, setPageProtocolo] = useState(0);
  const [totalPagesProtocolo, setTotalPagesProtocolo] = useState(0);

  const [remessas, setRemessas] = useState<any[]>([]);
  const [selectedProtocol, setSelectedProtocol] = useState<string | null>(null);
  const [detalhesRemessa, setDetalhesRemessa] = useState<any[]>([]);
  
  const [showProtocolFilters, setShowProtocolFilters] = useState(false);
  const [filtroProtCodigo, setFiltroProtCodigo] = useState('');
  const [filtroProtNome, setFiltroProtNome] = useState('');
  const [filtroProtProtocolo, setFiltroProtProtocolo] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroDtInicial, setFiltroDtInicial] = useState('');
  const [filtroDtFinal, setFiltroDtFinal] = useState('');

  // --- ESTADOS: TABELA DE CESSÕES (Visão 2) ---
  const [cessoesAbaLancamento, setCessoesAbaLancamento] = useState<Cessao[]>([]);
  const [pageLancamento, setPageLancamento] = useState(0);
  const [totalPagesLancamento, setTotalPagesLancamento] = useState(0);

  const [showCessaoFilters, setShowCessaoFilters] = useState(false);
  const [filtroCessaoCodigo, setFiltroCessaoCodigo] = useState('');
  const [filtroCessaoNome, setFiltroCessaoNome] = useState('');
  const [filtroCessaoDtInicial, setFiltroCessaoDtInicial] = useState('');
  const [filtroCessaoDtFinal, setFiltroCessaoDtFinal] = useState('');

  // --- ESTADOS: VISÃO 3 (LANÇAMENTO MANUAL) ---
  const [numeroCessao, setNumeroCessao] = useState('');
  const [nomeCessao, setNomeCessao] = useState('');
  const [dtBase1, setDtBase1] = useState(format(new Date(), 'dd/MM/yyyy'));
  const [dtBase2, setDtBase2] = useState(format(new Date(), 'dd/MM/yyyy'));
  
  const [availablePromotoras, setAvailablePromotoras] = useState<Promotora[]>([]);
  const [selectedPromotoras, setSelectedPromotoras] = useState<Promotora[]>([]);
  const [selectedToMovePromotoras, setSelectedToMovePromotoras] = useState<Promotora[]>([]);
  const [selectedFromSelectedPromotoras, setSelectedFromSelectedPromotoras] = useState<Promotora[]>([]);
  
  const [availableEmpregadores, setAvailableEmpregadores] = useState<Empregador[]>([]);
  const [selectedEmpregadores, setSelectedEmpregadores] = useState<Empregador[]>([]);
  const [selectedToMoveEmpregadores, setSelectedToMoveEmpregadores] = useState<Empregador[]>([]);
  const [selectedFromSelectedEmpregadores, setSelectedFromSelectedEmpregadores] = useState<Empregador[]>([]);
  
  const [selecionarTudo, setSelecionarTudo] = useState(true);
  const [operacoes, setOperacoes] = useState<OperacaoDTO[]>([]);
  const [empregadorPromotoraList, setEmpregadorPromotoraList] = useState<EmpregadorPromotoraCessaoDTO[]>([]);
  const [exibeDetalhe, setExibeDetalhe] = useState(false);
  const [exibeOperacao, setExibeOperacao] = useState(false);

  // =========================================================================
  // EFFECTS & API CALLS
  // =========================================================================
  
  useEffect(() => {
    if (view === 'PROTOCOLOS' && protocoloStep === 1) {
      carregarCessoesAbaProtocolo(pageProtocolo);
    } else if (view === 'LANCAMENTO' && lancamentoSubView === 'TABELA') {
      carregarCessoesAbaLancamento(pageLancamento);
    }
  }, [view, protocoloStep, lancamentoSubView, pageProtocolo, pageLancamento]);

  useEffect(() => {
    if (view === 'PROTOCOLOS' && protocoloStep === 2 && selectedCessaoRemessa) {
      carregarProtocolosDaCessao(selectedCessaoRemessa.id);
    }
  }, [protocoloStep, view, selectedCessaoRemessa]);

  const carregarCessoesAbaProtocolo = async (p = 0) => {
    setLoading(true);
    try {
      const response = await cessaoService.listarTodasCessoes(p, 10);
      if (response.data && response.data.content) {
        setCessoesAbaProtocolo(response.data.content);
        setTotalPagesProtocolo(response.data.totalPages);
      }
    } catch (err) { console.error("Erro listarTodasCessoes Aba 1:", err); }
    finally { setLoading(false); }
  };

  const carregarCessoesAbaLancamento = async (p = 0) => {
    setLoading(true);
    try {
      const response = await cessaoService.listarTodasCessoes(p, 10);
      if (response.data && response.data.content) {
        setCessoesAbaLancamento(response.data.content);
        setTotalPagesLancamento(response.data.totalPages);
      }
    } catch (err) { console.error("Erro listarTodasCessoes Aba 2:", err); }
    finally { setLoading(false); }
  };

  // BUSCA AS REMESSAS ESPECÍFICAS DA CESSÃO (Aba Protocolos - Step 2)
  const carregarProtocolosDaCessao = async (cessaoId: string | number) => {
    setLoading(true);
    try {
      const response = await remessaCessaoService.buscarOperacoesRemessa(Number(cessaoId)); 
      // A nova resposta da API é um Array de RemessaCessaoDetalheDTO
      const data = response.data;
      
      if (Array.isArray(data)) {
        setRemessas(data);
      } else {
        setRemessas([]);
      }
    } catch (error) { 
      console.error("Erro carregarProtocolosDaCessao:", error); 
      setRemessas([]);
      setAlert({ message: 'Erro ao buscar as remessas desta cessão.', type: 'error' });
    }
    finally { setLoading(false); }
  };

  // --- NAVEGAÇÃO E BOTÕES (ABA 1: PROTOCOLOS) ---
  const handleVisualizarCessaoRemessa = (cessao: any) => {
    setSelectedCessaoRemessa(cessao);
    setProtocoloStep(2);
  };

  const handleEnviarRemessaGeral = (cessao: any) => {
    if(window.confirm(`Deseja enviar a cessão: ${cessao.nome}?`)) {
      setAlert({ message: `Cessão enviada com sucesso!`, type: 'success' });
    }
  };

  const handleDownloadArquivoGeral = (cessao: any) => {
    setAlert({ message: `Iniciando download do arquivo da cessão: ${cessao.nome}...`, type: 'success' });
  };

  // Abre os contratos do Protocolo no Step 3
  const handleVisualizarProtocolo = (remessa: any) => {
    try {
      // Como o objeto já foi formatado pela API, pegamos direto de 'operacoes'
      if (remessa.operacoes && remessa.operacoes.length > 0) {
        setDetalhesRemessa(remessa.operacoes);
      } else {
        setDetalhesRemessa([]);
      }
      setSelectedProtocol(remessa.protocolo);
      setProtocoloStep(3);
    } catch (e) {
      console.error("Erro ao ler os dados da remessa:", e);
      setAlert({ message: 'Erro ao carregar detalhes dos contratos desta remessa.', type: 'error' });
      setDetalhesRemessa([]);
      setSelectedProtocol(remessa.protocolo);
      setProtocoloStep(3);
    }
  };

  const handleEnviarRemessa = (remessa: any) => {
    if(window.confirm(`Deseja gerar e enviar a remessa do protocolo ${remessa.protocolo}?`)) {
      setAlert({ message: `Remessa ${remessa.protocolo} enviada com sucesso!`, type: 'success' });
    }
  };

  const handleDownloadCessao = (remessa: any) => {
    setAlert({ message: `Iniciando download do protocolo ${remessa.protocolo}...`, type: 'success' });
  };

  // --- LÓGICA DA ABA 2: LANÇAMENTO MANUAL ---
  const listarCessaoParaManual = async (idBusca: string) => {
    if (!idBusca) return setAlert({ message: 'Preencha o campo de busca', type: 'warning' });
    setLoading(true);
    try {
      const responseList = await cessaoService.listarCessaoPorId(idBusca);
      setNomeCessao(responseList.data.nome);
      setNumeroCessao(idBusca);
      
      const resProm = await promotoraService.listarTodasPromotorasAtivas();
      setAvailablePromotoras(resProm.data);
      
      const resEmp = await empregadorService.listarTodosEmpregadoresAtivos();
      setAvailableEmpregadores(resEmp.data);

      setLancamentoSubView('CONFIG');
      setExibeDetalhe(true);
      setExibeOperacao(false);
      setOperacoes([]);
      setEmpregadorPromotoraList([]);
    } catch (err) { 
      setAlert({ message: 'Cessão não encontrada.', type: 'error' }); 
      setNomeCessao('');
      setExibeDetalhe(false);
    }
    finally { setLoading(false); }
  };

  // =========================================================================
  // LÓGICA DUAL-LIST (PROMOTORAS & EMPREGADORES) ORIGINAL
  // =========================================================================
  const moveToSelectedPromotoras = () => {
    const newSelected = [...selectedPromotoras, ...selectedToMovePromotoras];
    const newAvailable = availablePromotoras.filter((promotora) => !selectedToMovePromotoras.includes(promotora));
    setAvailablePromotoras(newAvailable);
    setSelectedPromotoras(newSelected);
    setSelectedToMovePromotoras([]);
  };

  const moveToAvailablePromotoras = () => {
    const newAvailable = [...availablePromotoras, ...selectedFromSelectedPromotoras];
    const newSelected = selectedPromotoras.filter((promotora) => !selectedFromSelectedPromotoras.includes(promotora));
    setAvailablePromotoras(newAvailable);
    setSelectedPromotoras(newSelected);
    setSelectedFromSelectedPromotoras([]);
  };

  const moveAllToSelectedPromotoras = () => {
    const newSelected = [...selectedPromotoras, ...availablePromotoras];
    setSelectedPromotoras(newSelected);
    setAvailablePromotoras([]);
  };

  const moveAllToAvailablePromotoras = () => {
    const newAvailable = [...availablePromotoras, ...selectedPromotoras];
    setAvailablePromotoras(newAvailable);
    setSelectedPromotoras([]);
  };

  const moveToSelectedEmpregadores = () => {
    const newSelected = [...selectedEmpregadores, ...selectedToMoveEmpregadores];
    const newAvailable = availableEmpregadores.filter((empregador) => !selectedToMoveEmpregadores.includes(empregador));
    setAvailableEmpregadores(newAvailable);
    setSelectedEmpregadores(newSelected);
    setSelectedToMoveEmpregadores([]);
  };

  const moveToAvailableEmpregadores = () => {
    const newAvailable = [...availableEmpregadores, ...selectedFromSelectedEmpregadores];
    const newSelected = selectedEmpregadores.filter((empregador) => !selectedFromSelectedEmpregadores.includes(empregador));
    setAvailableEmpregadores(newAvailable);
    setSelectedEmpregadores(newSelected);
    setSelectedFromSelectedEmpregadores([]);
  };

  const moveAllToSelectedEmpregadores = () => {
    const newSelected = [...selectedEmpregadores, ...availableEmpregadores];
    setSelectedEmpregadores(newSelected);
    setAvailableEmpregadores([]);
  };

  const moveAllToAvailableEmpregadores = () => {
    const newAvailable = [...availableEmpregadores, ...selectedEmpregadores];
    setAvailableEmpregadores(newAvailable);
    setSelectedEmpregadores([]);
  };

  // =========================================================================
  // CONSULTA DE OPERAÇÕES E CÁLCULOS ORIGINAL
  // =========================================================================
  const consultarOperacoes = async () => {
    if (selectedPromotoras.length <= 0) return setAlert({ message: 'Selecione ao menos uma Promotora', type: 'warning' });
    if (selectedEmpregadores.length <= 0) return setAlert({ message: 'Selecione ao menos um Empregador', type: 'warning' });
    if ((dtBase1 !== '' && dtBase2 === '') || dtBase1 === '' && dtBase2 !== '') return setAlert({ message: 'Preencha os campos de datas ou limpe os dois!', type: 'warning' });
    if (dtBase1 !== '' && dtBase2 !== '' && parse(dtBase1, "dd/MM/yyyy", new Date()) > parse(dtBase2, "dd/MM/yyyy", new Date())) return setAlert({ message: 'Data inicial deve ser inferior a data final', type: 'warning' });
    
    try {
      const dto: EmpregadorPromotoraDTO = new EmpregadorPromotoraDTO();
      dto.empregadores = selectedEmpregadores;
      dto.promotoras = selectedPromotoras;
      dto.dtBase1 = parse(dtBase1, "dd/MM/yyyy", new Date());
      dto.dtBase2 = parse(dtBase2, "dd/MM/yyyy", new Date());
      dto.idCessao = parseInt(numeroCessao);

      setLoading(true);
      const responseList = await operacaoService.listarOperacoesCessaoPorPromotoraEmpregador(dto);
      const json = responseList.data;
      setEmpregadorPromotoraList(json.empregadorPromotora);
      setOperacoes(json.operacoes);
      setExibeOperacao(true);
      setLoading(false);
    } catch (err) {
      setExibeOperacao(true);
      setLoading(false);
      console.error(err);
    }
  };

  const lancarCessao = async () => {
    try {
      setLoading(true);
      const response = await operacaoService.cederOperacoesCessao(numeroCessao, operacoes);
      setExibeOperacao(true);
      setLoading(false);
      setAlert({ message: response.data, type: 'success' });
    } catch (err: any) {
      setExibeOperacao(true);
      setLoading(false);
      console.error(err);
      setAlert({ message: err.response?.data || 'Erro ao lançar', type: 'error' });
    }
  };

  const totalOperacoes = empregadorPromotoraList.reduce((acc, item) => acc + item.qtdOperacoes, 0);
  const totalFinanciado = empregadorPromotoraList.reduce((acc, item) => acc + item.valorFinanciado, 0);
  const totalBruto = empregadorPromotoraList.reduce((acc, item) => acc + item.valorBruto, 0);
  const totalCessao = empregadorPromotoraList.reduce((acc, item) => acc + item.valorCessao, 0);

  const todosMarcados = (operacoes: OperacaoDTO[]) => {
    return operacoes.every((item) => item.ativo);
  };

  const handleAtivoChange = (id: any) => {
    if (selecionarTudo) setSelecionarTudo(false);
    setOperacoes((prevData) => {
      const updatedOperacoes = prevData.map((item) => item.numeroContrato === id ? { ...item, ativo: !item.ativo } : item);
      updateEmpregadorPromotoraTotais(updatedOperacoes);
      setSelecionarTudo(todosMarcados(updatedOperacoes));
      return updatedOperacoes;
    });
  };

  const selecionarDeselecionarTodos = (marcar: boolean) => {
    setSelecionarTudo(marcar);
    const updatedOperacoes = operacoes.map((item) => ({ ...item, ativo: marcar }));
    setOperacoes(updatedOperacoes);
    updateEmpregadorPromotoraTotais(updatedOperacoes);
  };

  const updateEmpregadorPromotoraTotais = (updatedOperacoes: OperacaoDTO[]) => {
    const novosTotais: Record<string, { qtdOperacoes: number; valorFinanciado: number; valorBruto: number; valorCessao: number }> = {};
    updatedOperacoes.forEach((item) => {
      if (item.ativo) {
        const chave = `${item.empregador}-${item.promotora}`;
        if (!novosTotais[chave]) {
          novosTotais[chave] = { qtdOperacoes: 0, valorFinanciado: 0, valorBruto: 0, valorCessao: 0 };
        }
        novosTotais[chave].qtdOperacoes += 1;
        novosTotais[chave].valorFinanciado += item.valorFinan ?? 0;
        novosTotais[chave].valorBruto += item.vlrBruto ?? 0;
        novosTotais[chave].valorCessao += item.valorCedido ?? 0;
      }
    });

    setEmpregadorPromotoraList((prevData) =>
      prevData.map((item) => {
        const chave = `${item.empregador}-${item.promotora}`;
        const novosTotaisItem = novosTotais[chave];
        if (novosTotaisItem) {
          return {
            ...item,
            qtdOperacoes: novosTotaisItem.qtdOperacoes,
            valorFinanciado: novosTotaisItem.valorFinanciado,
            valorBruto: novosTotaisItem.valorBruto,
            valorCessao: novosTotaisItem.valorCessao
          };
        }
        return { ...item, qtdOperacoes: 0, valorFinanciado: 0, valorBruto: 0, valorCessao: 0 };
      })
    );
  };

  // =========================================================================
  // RENDER
  // =========================================================================
  return (
    <div className="position-relative m-md-3">
      {alert && (<CustomAlert message={alert.message} type={alert.type} onClose={() => { setAlert(null); if (alert.type === 'success') { window.location.reload(); } }} />)}

      {/* CABEÇALHO GERAL DA TELA */}
      <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h5>
          {view === 'PROTOCOLOS' && protocoloStep === 1 && 'Protocolos de Cessão - Cessões Encontradas'}
          {view === 'PROTOCOLOS' && protocoloStep === 2 && `Remessas da Cessão: ${selectedCessaoRemessa?.nome}`}
          {view === 'PROTOCOLOS' && protocoloStep === 3 && `Detalhes do Protocolo: ${selectedProtocol}`}
          {view === 'LANCAMENTO' && 'Cessão > Cessão de Carteira'}
        </h5>
        <div className="d-flex gap-2">
          {view === 'PROTOCOLOS' && protocoloStep === 1 && (
            <Button variant="outline-secondary" onClick={() => setShowProtocolFilters(!showProtocolFilters)}>
              <FontAwesomeIcon icon={faFilter} className="me-2" /> Filtros
            </Button>
          )}
          <Button 
            id="App-button-cor-principal-escura"
            onClick={() => {
              if(view === 'PROTOCOLOS') { 
                setView('LANCAMENTO'); 
                setLancamentoSubView('TABELA');
                setPageLancamento(0); 
              } else { 
                setView('PROTOCOLOS'); 
                setProtocoloStep(1);
                setPageProtocolo(0);
              }
            }}
          >
            {view === 'PROTOCOLOS' ? 'Lançamento de Cessão' : 'Voltar para Protocolos'}
          </Button>
        </div>
      </div>

      {loading ? <Spinner loading={loading} /> : (
        <>
          {/* ============================================================= */}
          {/* VISÃO 1: ABA DE PROTOCOLOS (DIVIDIDA EM 3 STEPS)              */}
          {/* ============================================================= */}
          {view === 'PROTOCOLOS' && (
            <div className="card shadow-sm p-3">
              
              {/* STEP 1: LISTA DE CESSÕES GERAIS */}
              {protocoloStep === 1 && (
                <>
                  {showProtocolFilters && (
                    <div className="mb-3 p-3 border rounded bg-light">
                      <Row className="align-items-end g-2 mb-3">
                        <Form.Group as={Col} md="2">
                          <Form.Label>Código</Form.Label>
                          <Form.Control type="text" value={filtroProtCodigo} onChange={(e) => setFiltroProtCodigo(e.target.value)} />
                        </Form.Group>
                        <Form.Group as={Col} md="4">
                          <Form.Label>Nome</Form.Label>
                          <Form.Control type="text" value={filtroProtNome} onChange={(e) => setFiltroProtNome(e.target.value)} />
                        </Form.Group>
                        <Form.Group as={Col} md="3">
                          <Form.Label>Protocolo</Form.Label>
                          <Form.Control type="text" value={filtroProtProtocolo} onChange={(e) => setFiltroProtProtocolo(e.target.value)} />
                        </Form.Group>
                        <Form.Group as={Col} md="3">
                          <Form.Label>Status</Form.Label>
                          <Form.Select value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)}>
                            <option value="">Todos</option>
                            <option value="CRIADA">Criada</option>
                            <option value="ENVIADA">Enviada</option>
                            <option value="FALHA">Falha</option>
                            <option value="ERRO">Erro</option>
                          </Form.Select>
                        </Form.Group>
                      </Row>
                      <Row className="align-items-end g-2">
                        <Form.Group as={Col} md="5">
                          <Form.Label>Período Inicial / Final</Form.Label>
                          <div className="d-flex align-items-center">
                            <Form.Control type="date" size="sm" value={filtroDtInicial} onChange={(e) => setFiltroDtInicial(e.target.value)}/>
                            <span className="mx-2">à</span>
                            <Form.Control type="date" size="sm" value={filtroDtFinal} onChange={(e) => setFiltroDtFinal(e.target.value)}/>
                          </div>
                        </Form.Group>
                        <Col md="2" className="d-grid ms-auto">
                          <Button id="App-button-cor-principal" onClick={() => carregarCessoesAbaProtocolo(0)}>
                            <FontAwesomeIcon icon={faSearch} className="me-2" /> Buscar
                          </Button>
                        </Col>
                      </Row>
                    </div>
                  )}

                  <div className="table-responsive">
                    <Table striped bordered hover>
                      <thead className="table-light">
                        <tr>
                          <th>Código</th>
                          <th>Nome da Cessão</th>
                          <th>Data Base</th>
                          <th>Valor</th>
                          <th className="text-center">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cessoesAbaProtocolo.map((cessao) => (
                          <tr key={cessao.id}>
                            <td className="align-middle">{cessao.codigo}</td>
                            <td className="align-middle">{cessao.nome}</td>
                            <td className="align-middle">{cessao.dtInclusao ? format(new Date(cessao.dtInclusao), 'dd/MM/yyyy') : ''}</td>
                            <td className="align-middle">R$ {cessao.valor?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                            <td className="text-center">
                              <Button variant="link" className="p-0 me-3 text-info" title="Visualizar Remessas" onClick={() => handleVisualizarCessaoRemessa(cessao)}>
                                <FontAwesomeIcon icon={faEye} />
                              </Button>
                              <Button variant="link" className="p-0 me-3 text-primary" title="Enviar Remessas" onClick={() => handleEnviarRemessaGeral(cessao)}>
                                <FontAwesomeIcon icon={faPaperPlane} />
                              </Button>
                              <Button variant="link" className="p-0 text-success" title="Download de Arquivos" onClick={() => handleDownloadArquivoGeral(cessao)}>
                                <FontAwesomeIcon icon={faDownload} />
                              </Button>
                            </td>
                          </tr>
                        ))}
                        {cessoesAbaProtocolo.length === 0 && (
                          <tr><td colSpan={5} className="text-center text-muted">Nenhuma cessão encontrada.</td></tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                  <div className="d-flex justify-content-center mt-3">
                    <PaginationCompacta page={pageProtocolo} totalPages={totalPagesProtocolo} onPageChange={setPageProtocolo} />
                  </div>
                </>
              )}

              {/* STEP 2: LISTA DE REMESSAS DA CESSÃO CLICADA */}
              {protocoloStep === 2 && (
                <>
                  <div className="mb-3">
                    <Button variant="secondary" size="sm" onClick={() => setProtocoloStep(1)}>
                      <FontAwesomeIcon icon={faArrowLeft} className="me-2" /> Voltar para Cessões
                    </Button>
                  </div>
                  <div className="table-responsive">
                    <Table striped bordered hover>
                      <thead className="table-light">
                        <tr>
                          <th>Protocolo</th>
                          <th>Status</th>
                          <th style={{width: '35%'}}>Mensagem</th>
                          <th>Data de Criação</th>
                          <th className="text-center">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {remessas.map((remessa, idx) => (
                          <tr key={remessa.idRemessa || idx}>
                            <td className="align-middle"><strong>{remessa.protocolo}</strong></td>
                            <td className="align-middle">
                              <Badge bg={remessa.status === 'ENVIADA' ? 'success' : remessa.status === 'FALHA' ? 'danger' : 'secondary'}>
                                {remessa.status || 'N/A'}
                              </Badge>
                            </td>
                            <td className="align-middle" style={{ maxWidth: '300px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={remessa.mensagem}>
                              {remessa.mensagem || 'Processado'}
                            </td>
                            <td className="align-middle">{remessa.dtaCriacao ? new Date(remessa.dtaCriacao).toLocaleString() : 'N/A'}</td>
                            <td className="text-center align-middle">
                              <Button variant="link" className="p-0 me-3 text-info" title="Visualizar Contratos" onClick={() => handleVisualizarProtocolo(remessa)}>
                                <FontAwesomeIcon icon={faEye} />
                              </Button>
                              <Button variant="link" className="p-0 me-3 text-primary" title="Enviar Remessa" onClick={() => handleEnviarRemessa(remessa)}>
                                <FontAwesomeIcon icon={faPaperPlane} />
                              </Button>
                              <Button variant="link" className="p-0 text-success" title="Download Arquivo" onClick={() => handleDownloadCessao(remessa)}>
                                <FontAwesomeIcon icon={faDownload} />
                              </Button>
                            </td>
                          </tr>
                        ))}
                        {remessas.length === 0 && (
                          <tr><td colSpan={5} className="text-center text-muted">Nenhum protocolo encontrado para esta cessão.</td></tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </>
              )}

              {/* STEP 3: CONTRATOS DA REMESSA CLICADA (JSON MAP) */}
              {protocoloStep === 3 && (
                <>
                  <div className="mb-3">
                    <Button variant="secondary" size="sm" onClick={() => setProtocoloStep(2)}>
                      <FontAwesomeIcon icon={faArrowLeft} className="me-2" /> Voltar para Remessas
                    </Button>
                  </div>
                  <div className="table-responsive">
                    <Table striped bordered hover>
                      <thead className="table-dark">
                        <tr>
                          <th>Nº Contrato</th>
                          <th>ID Operação</th>
                          <th>Valor Cedido</th>
                          <th>Data Inclusão</th>
                          <th>Quant. Parcelas</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detalhesRemessa.length > 0 ? (
                          detalhesRemessa.map((contrato: any, index: number) => (
                            <tr key={index}>
                              <td>{contrato.numeroContrato}</td>
                              <td>{contrato.idOperacao}</td>
                              <td>R$ {Number(contrato.valorCedido || 0).toLocaleString('pt-BR', {minimumFractionDigits: 2})}</td>
                              <td>{contrato.dtInclusao ? new Date(contrato.dtInclusao).toLocaleDateString() : ''}</td>
                              <td>{contrato.quantidadeParcelasCedidas}</td>
                            </tr>
                          ))
                        ) : (
                          <tr><td colSpan={6} className="text-center text-muted">Nenhum contrato encontrado neste protocolo.</td></tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ============================================================= */}
          {/* VISÃO 2: ABA DE LANÇAMENTO (TABELA E CONFIGURAÇÃO MANUAL)     */}
          {/* ============================================================= */}
          {view === 'LANCAMENTO' && (
            <>
              {lancamentoSubView === 'TABELA' ? (
                <div className="card shadow-sm p-3">
                  
                  <div className="d-flex justify-content-between mb-3">
                    <Button variant="outline-secondary" onClick={() => setShowCessaoFilters(!showCessaoFilters)}>
                      <FontAwesomeIcon icon={faFilter} className="me-2" /> Filtros
                    </Button>
                    <Button id="App-button-cor-principal-escura" onClick={() => navigate('/backoffice-tabelas/cessao/inclusao', { state: { id: null } })}>
                      + Nova Cessão
                    </Button>
                  </div>

                  {/* FILTROS DA TABELA DE CESSÕES (Aba Lançamento) */}
                  {showCessaoFilters && (
                    <div className="mb-3 p-3 border rounded bg-light">
                      <Row className="align-items-end g-2">
                        <Form.Group as={Col} md="2">
                          <Form.Label>Código</Form.Label>
                          <Form.Control type="text" value={filtroCessaoCodigo} onChange={(e) => setFiltroCessaoCodigo(e.target.value)} />
                        </Form.Group>
                        <Form.Group as={Col} md="4">
                          <Form.Label>Nome da Cessão</Form.Label>
                          <Form.Control type="text" value={filtroCessaoNome} onChange={(e) => setFiltroCessaoNome(e.target.value)} />
                        </Form.Group>
                        <Form.Group as={Col} md="4">
                          <Form.Label>Período (Data Base)</Form.Label>
                          <div className="d-flex align-items-center">
                            <Form.Control type="date" size="sm" value={filtroCessaoDtInicial} onChange={(e) => setFiltroCessaoDtInicial(e.target.value)}/>
                            <span className="mx-1">à</span>
                            <Form.Control type="date" size="sm" value={filtroCessaoDtFinal} onChange={(e) => setFiltroCessaoDtFinal(e.target.value)}/>
                          </div>
                        </Form.Group>
                        <Col md="2" className="d-grid">
                          <Button id="App-button-cor-principal" onClick={() => carregarCessoesAbaLancamento(0)}>
                            <FontAwesomeIcon icon={faSearch} className="me-2" /> Buscar
                          </Button>
                        </Col>
                      </Row>
                    </div>
                  )}

                  <div className="table-responsive">
                    <Table bordered striped hover className="table-clickable">
                      <thead className="table-light">
                        <tr>
                          <th>Código</th>
                          <th>Nome</th>
                          <th>Data Base</th>
                          <th>Valor</th>
                          <th>Taxa %</th>
                          <th className="text-center">Ação</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cessoesAbaLancamento.map((item) => (
                          <tr key={item.id} style={{ cursor: 'pointer' }} onClick={() => listarCessaoParaManual(item.id!.toString())}>
                            <td className="align-middle">{item.codigo}</td>
                            <td className="align-middle">{item.nome}</td>
                            <td className="align-middle">{item.dtInclusao ? format(new Date(item.dtInclusao), 'dd/MM/yyyy') : ''}</td>
                            <td className="align-middle">R$ {item.valor?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                            <td className="align-middle">{item.taxa}</td>
                            <td className="text-center align-middle">
                              <Button 
                                id="App-button-cor-principal" 
                                size="sm" 
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  listarCessaoParaManual(item.id!.toString()); 
                                }}
                              >
                                <FontAwesomeIcon icon={faPaperPlane} className="me-1" /> Lançar Cessão
                              </Button>
                            </td>
                          </tr>
                        ))}
                        {cessoesAbaLancamento.length === 0 && (
                          <tr><td colSpan={6} className="text-center text-muted">Nenhuma cessão encontrada.</td></tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                  <div className="d-flex justify-content-center mt-3">
                    <PaginationCompacta page={pageLancamento} totalPages={totalPagesLancamento} onPageChange={setPageLancamento} />
                  </div>
                </div>
              ) : (
                /* ================= CONFIGURAÇÃO MANUAL (ORIGINAL) ================= */
                <div className="card" style={{ marginTop: '25px' }}>
                  <div className="card-body">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <strong>Lançamento de Cessão de Carteira - Manual</strong>
                      <Button variant="secondary" size="sm" onClick={() => setLancamentoSubView('TABELA')}>
                        <FontAwesomeIcon icon={faArrowLeft} className="me-2"/> Voltar
                      </Button>
                    </div>
                    <div className="row" style={{ marginTop: '10px' }}>
                      <div className="col-md-2">
                        <div className="form-group">
                          <label>Código Cessão <span style={{ color: 'red' }}>*</span></label>
                          <input type="text" className="form-control form-control-sm brds " style={{ color: 'Black', backgroundColor: 'White' }}
                            value={numeroCessao} onChange={(e) => { setNumeroCessao((e.target.value)); setExibeDetalhe(false); setNomeCessao(''); setExibeOperacao(false); setOperacoes([]); setEmpregadorPromotoraList([]); }} />
                        </div>
                      </div>
                      <div className="col-md-2">
                        <button className="input-group-btn"
                          style={{
                            backgroundColor: 'white', width: '30%', marginLeft: '10px', display: 'flex', alignItems: 'center',
                            justifyContent: 'center', border: '1px solid LightGrey', borderRadius: '5px', height: '54px', minWidth: '50px'
                          }} onClick={() => listarCessaoParaManual(numeroCessao)}>
                          <a >
                            <FontAwesomeIcon icon={faSearch} style={{ color: 'black' }} />
                          </a>
                        </button>
                      </div>
                    </div>

                    {exibeDetalhe ? (
                      <>
                        <div className="" style={{ marginTop: '10px' }}>
                          <div className="row">
                            <div className="col-md-3" style={{ paddingRight: '10px' }}>
                              <label style={{ width: '100%' }}>Nome Cessão</label>
                            </div>
                            <div className="col-md-2" style={{ paddingLeft: '10px', paddingRight: '10px' }}>
                              <label>Faixa de Datas Base <span style={{ color: 'red' }}>*</span></label>
                            </div>
                          </div>
                          <div className="row" style={{ marginTop: '5px' }}>
                            <div className="col-md-3" style={{ paddingRight: '10px' }}>
                              <input type="text" value={nomeCessao} disabled className="form-control form-control-sm brds " style={{ color: 'Black', backgroundColor: 'LightGrey' }} />
                            </div>
                            <div className="col-md-4">
                              <thead>
                                <tr>
                                  <th style={{ width: "150px" }}><InputMask required mask="99/99/9999" className="form-control" type="text" value={dtBase1} onChange={(e) => setDtBase1((e.target.value).replace(/_/g, ""))} /></th>
                                  <th style={{ width: "15px" }}> a </th>
                                  <th style={{ width: "150px" }}><InputMask required mask="99/99/9999" className="form-control" type="text" value={dtBase2} onChange={(e) => setDtBase2((e.target.value).replace(/_/g, ""))} /></th>
                                </tr>
                              </thead>
                            </div>
                          </div>
                        </div>

                        <div style={{ display: 'flex', marginTop: '10px' }}>
                          <div>
                            <h6>Promotoras Disponíveis</h6>
                            <select
                              multiple
                              value={selectedToMovePromotoras.map((p) => p.id?.toString() || '')}
                              onChange={(e) => {
                                const options = e.target.options;
                                const selected = [];
                                for (let i = 0; i < options.length; i++) {
                                  if (options[i].selected) {
                                    const promotora = availablePromotoras.find(p => p.id?.toString() === options[i].value);
                                    if (promotora) selected.push(promotora);
                                  }
                                }
                                setSelectedToMovePromotoras(selected);
                              }}
                              style={{ width: '575px', height: '250px' }}
                            >
                              {availablePromotoras.map((promotora) => (
                                <option key={promotora.id} value={promotora.id?.toString() || ''}>
                                  {promotora.id} - {promotora.nome!}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <button onClick={moveToSelectedPromotoras} style={{ margin: '5px' }}>{'>'}</button>
                            <button onClick={moveAllToSelectedPromotoras} style={{ margin: '5px' }}>{'>>'}</button>
                            <button onClick={moveToAvailablePromotoras} style={{ margin: '5px' }}>{'<'}</button>
                            <button onClick={moveAllToAvailablePromotoras} style={{ margin: '5px' }}>{'<<'}</button>
                          </div>
                          <div>
                            <h6>Promotoras Selecionadas</h6>
                            <select
                              multiple
                              value={selectedFromSelectedPromotoras.map((p) => p.id?.toString() || '')}
                              onChange={(e) => {
                                const options = e.target.options;
                                const selected = [];
                                for (let i = 0; i < options.length; i++) {
                                  if (options[i].selected) {
                                    const promotora = selectedPromotoras.find(p => p.id?.toString() === options[i].value);
                                    if (promotora) selected.push(promotora);
                                  }
                                }
                                setSelectedFromSelectedPromotoras(selected);
                              }}
                              style={{ width: '575px', height: '250px' }}
                            >
                              {selectedPromotoras.map((promotora) => (
                                <option key={promotora.id} value={promotora.id?.toString() || ''}>
                                  {promotora.id} - {promotora.nome!}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div style={{ display: 'flex', marginTop: '10px' }}>
                          <div>
                            <h6>Empregadores Disponíveis </h6>
                            <select
                              multiple
                              value={selectedToMoveEmpregadores.map((e) => e.id?.toString() || '')}
                              onChange={(e) => {
                                const options = e.target.options;
                                const selected = [];
                                for (let i = 0; i < options.length; i++) {
                                  if (options[i].selected) {
                                    const empregador = availableEmpregadores.find(e => e.id?.toString() === options[i].value);
                                    if (empregador) selected.push(empregador);
                                  }
                                }
                                setSelectedToMoveEmpregadores(selected);
                              }}
                              style={{ width: '575px', height: '250px' }}
                            >
                              {availableEmpregadores.map((empregador) => (
                                <option key={empregador.id} value={empregador.id?.toString() || ''} >
                                  {empregador.id} - {empregador.nome!}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <button onClick={moveToSelectedEmpregadores} style={{ margin: '5px' }}> {'>'} </button>
                            <button onClick={moveAllToSelectedEmpregadores} style={{ margin: '5px' }}> {'>>'} </button>
                            <button onClick={moveToAvailableEmpregadores} style={{ margin: '5px' }}> {'<'} </button>
                            <button onClick={moveAllToAvailableEmpregadores} style={{ margin: '5px' }}> {'<<'} </button>
                          </div>
                          <div>
                            <h6>Empregadores Selecionados </h6>
                            <select
                              multiple
                              value={selectedFromSelectedEmpregadores.map((e) => e.id?.toString() || '')}
                              onChange={(e) => {
                                const options = e.target.options;
                                const selected = [];
                                for (let i = 0; i < options.length; i++) {
                                  if (options[i].selected) {
                                    const empregador = selectedEmpregadores.find(e => e.id?.toString() === options[i].value);
                                    if (empregador) selected.push(empregador);
                                  }
                                }
                                setSelectedFromSelectedEmpregadores(selected);
                              }}
                              style={{ width: '575px', height: '250px' }}
                            >
                              {selectedEmpregadores.map((empregador) => (
                                <option key={empregador.id} value={empregador.id?.toString() || ''} >
                                  {empregador.id} - {empregador.nome!}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div className="row" style={{ marginTop: '15px' }}>
                          <div className="col-md-3">
                            <button className="input-group-btn" id="App-button-cor-principal"
                              style={{
                                width: '80%', display: 'flex', alignItems: 'center',
                                justifyContent: 'center', border: '1px solid LightGrey', borderRadius: '5px', height: '50px', minWidth: '50px'
                              }} onClick={(e) => { consultarOperacoes(); setOperacoes([]); setEmpregadorPromotoraList([]) }}>
                              <a>
                                <FontAwesomeIcon icon={faSearch} style={{ color: 'White', fontSize: 'larger', marginTop: '5px' }} />
                              </a>
                              <strong style={{ color: 'White', marginLeft: '5px', fontSize: 'larger' }}> Consultar Operações</strong>
                            </button>
                          </div>
                        </div>

                        {empregadorPromotoraList != null && empregadorPromotoraList.length > 0 ? (
                          <div style={{ maxHeight: '250px' }} className="table-responsive">
                            <div style={{ width: '100%', borderWidth: '1px', marginTop: '10px' }}>
                              <table cellSpacing="2" cellPadding="4" rules="all" style={{ border: '3', color: 'Black', borderColor: '#999999', borderWidth: '3px', borderStyle: 'Solid', width: '100%' }} className="table table-bordered table-striped">
                                <thead>
                                  <tr className="GridHeaderStyle" style={{ backgroundColor: 'white', color: 'black', position: 'sticky', top: '0' }} >
                                    <th style={{ textAlign: 'center' }} scope="col" id="App-header-table">Nome Empregador</th>
                                    <th style={{ textAlign: 'center' }} scope="col" id="App-header-table">Nome Promotora</th>
                                    <th style={{ textAlign: 'center' }} scope="col" id="App-header-table">Qtd Operações</th>
                                    <th style={{ textAlign: 'center' }} scope="col" id="App-header-table">Valor Financiado</th>
                                    <th style={{ textAlign: 'center' }} scope="col" id="App-header-table">Valor Bruto</th>
                                    <th style={{ textAlign: 'center' }} scope="col" id="App-header-table">Valor Cessão</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {empregadorPromotoraList.map((item) => (
                                    <tr className="GridRowStyle" key={`${item.empregador}-${item.promotora}`}>
                                      <td style={{ textAlign: 'center' }}>{item.empregador}</td>
                                      <td style={{ textAlign: 'center' }}>{item.promotora}</td>
                                      <td style={{ textAlign: 'center' }}>{item.qtdOperacoes}</td>
                                      <td style={{ textAlign: 'center' }}>R$ {item.valorFinanciado!.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                      <td style={{ textAlign: 'center' }}>R$ {item.valorBruto!.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                      <td style={{ textAlign: 'center' }}>R$ {item.valorCessao!.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                    </tr>
                                  ))}
                                  <tr>
                                    <td></td>
                                    <td></td>
                                    <td style={{ textAlign: 'center' }}>{totalOperacoes}</td>
                                    <td style={{ textAlign: 'center' }}>R$ {totalFinanciado.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                    <td style={{ textAlign: 'center' }}>R$ {totalBruto.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                    <td style={{ textAlign: 'center' }}>R$ {totalCessao.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ) : exibeOperacao ? (
                          <div style={{ width: '100%', borderWidth: '1px', marginTop: '10px' }}>
                            <span style={{ color: 'red' }}>Não existem operações disponíveis para cessão na data informada!</span>
                          </div>
                        ) : null}

                        {exibeOperacao && operacoes != null && operacoes.length > 0 ? (
                          <>
                            <form>
                              <h4><strong>Operações Disponíveis</strong></h4>
                              <div style={{ maxHeight: '1000px' }} className="table-responsive">
                                <div style={{ width: '100%', borderWidth: '1px', marginTop: '10px' }}>
                                  <table cellSpacing="2" cellPadding="4" rules="all" style={{ border: '3', color: 'Black', borderColor: '#999999', borderWidth: '3px', borderStyle: 'Solid', width: '100%' }} className="table table-bordered table-striped">
                                    <thead>
                                      <tr className="GridHeaderStyle" style={{ backgroundColor: 'white', color: 'black', position: 'sticky', top: '0' }}>
                                        <th scope="col" id="App-header-table"><input type="checkbox" checked={selecionarTudo} onChange={() => selecionarDeselecionarTodos(!selecionarTudo)} /> Tudo</th>
                                        <th scope="col" id="App-header-table">CPF</th>
                                        <th scope="col" id="App-header-table">Nº Contrato</th>
                                        <th scope="col" id="App-header-table">Data Base</th>
                                        <th scope="col" id="App-header-table">Empregador</th>
                                        <th scope="col" id="App-header-table">Prazo</th>
                                        <th scope="col" id="App-header-table">Valor Parcela</th>
                                        <th scope="col" id="App-header-table">Valor Financiado</th>
                                        <th scope="col" id="App-header-table">Valor Bruto</th>
                                        <th scope="col" id="App-header-table">Valor Cedido</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {operacoes.map((item) => (
                                        <tr key={item.numeroContrato}>
                                          <td><input type="checkbox" checked={item.ativo} onChange={() => handleAtivoChange(item.numeroContrato)} /></td>
                                          <td>{item.cpfCliente}</td>
                                          <td>{item.numeroContrato}</td>
                                          <td>{item.dtBase ? format(new Date(item.dtBase), 'dd/MM/yyyy') : ''}</td>
                                          <td>{item.empregador}</td>
                                          <td>{item.prazo}</td>
                                          <td>
                                            {typeof item.valorParcela === 'number'
                                              ? item.valorParcela.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                                              : '-'}
                                          </td>
                                          <td>R$ {item.valorFinanciado?.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? '0,00'}</td>
                                          <td>R$ {item.valorBruto?.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? '0,00'}</td>
                                          <td>R$ {item.valorCedido?.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? '0,00'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </form>
                            <div className="row" style={{ marginTop: '20px', height: '50px', maxWidth: '70%' }}>
                              <div className="col-md-3">
                                <button className="input-group-btn" id="App-button-cor-principal"
                                  style={{
                                    width: '80%', display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', border: '1px solid LightGrey', borderRadius: '5px', height: '100%', minWidth: '50px'
                                  }} onClick={lancarCessao}>
                                  <a>
                                    <FontAwesomeIcon icon={faSave} style={{ color: 'White', fontSize: 'larger', marginTop: '5px' }} />
                                  </a>
                                  <strong style={{ color: 'White', marginLeft: '5px', fontSize: 'larger' }}> Lançar Cessão</strong>
                                </button>
                              </div>
                            </div>
                          </>
                        ) : null}
                      </>
                    ) : null}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
      <ScrollToTopButton />
    </div>
  );
}